import {
  Constants,
  type Duration,
  type Enums,
  type MarketEnum,
  type PatternSpecificationEnum,
  type PositionEnum,
  type Setup,
  type StageEnum,
  directionList,
  durationList,
  marketList,
  patternSpecificationList,
} from '@hvf/contracts'
import { debounce } from '@solid-primitives/scheduled'
import { createSignal } from 'solid-js'

export type RequestSource = 'default' | 'taken' | 'subscribed'
export type Sort =
  | 'recent-activity'
  | 'recently-submitted'
  | 'subscribers'
  | 'takers'
  | 'PHQ'
  | 'community-score'

// Create a union type that includes literal null
export type PatternSpecFilterType = PatternSpecificationEnum | null

export type SetupFilter = {
  sort: Sort
  assetType: MarketEnum[]
  stage: Enums<'stage'>[]
  position: PositionEnum[]
  duration: Duration[]
  symbols: string[]
  patternSpecification: PatternSpecFilterType[] // Updated type
  /** Matches title, symbol, exchange or description */
  search: string
  exchanges: string[]
  /** profile id — set when arriving from a member result in global search */
  author: string | null
}

export const defaults: SetupFilter = {
  sort: 'recent-activity',
  symbols: [],
  duration: durationList,
  stage: Constants.public.Enums.stage.filter((stage) => stage !== 'spoiled'),
  position: directionList,
  // Pass literal null into the defaults
  patternSpecification: [...patternSpecificationList, null] as PatternSpecFilterType[],
  assetType: marketList,
  search: '',
  exchanges: [],
  author: null,
}

/** Number of filters that differ from the defaults (sort and the search text don't count) */
export const countActiveFilters = (f: SetupFilter) => {
  const keys: (keyof SetupFilter)[] = [
    'assetType', 'stage', 'position', 'duration', 'symbols', 'patternSpecification', 'exchanges', 'author',
  ]
  return keys.filter((k) => JSON.stringify(f[k]) !== JSON.stringify(defaults[k])).length
}

export function useFilters() {
  const [filters, setFilters] = createSignal<SetupFilter>(defaults)
  const [fetchFilters, setFetchFilters] = createSignal<SetupFilter>(defaults)
  const [page, setPage] = createSignal(1)
  const [searchInput, setSearchInputRaw] = createSignal('')

  const debouncedSetFilters = debounce((newFilters: SetupFilter) => {
    setFilters(newFilters)
    setPage(1)
  }, 300)

  const runFilters = () => {
    setFetchFilters(filters())
  }

  const setSort = (sort: Sort) => debouncedSetFilters({ ...filters(), sort })
  const setSymbols = (symbols: string[]) => debouncedSetFilters({ ...filters(), symbols })
  const setAssetType = (assetType: MarketEnum[]) => debouncedSetFilters({ ...filters(), assetType })
  const setDuration = (duration: Duration[]) => debouncedSetFilters({ ...filters(), duration })
  const setPosition = (position: PositionEnum[]) => debouncedSetFilters({ ...filters(), position })
  const setStage = (stage: StageEnum[]) => debouncedSetFilters({ ...filters(), stage })
  const setExchanges = (exchanges: string[]) => debouncedSetFilters({ ...filters(), exchanges })

  // Update parameter type here
  const setPatternSpecification = (patternSpecification: PatternSpecFilterType[]) =>
    debouncedSetFilters({ ...filters(), patternSpecification })

  // The search box and filter chips apply immediately rather than waiting for the drawer to close
  const applyNow = (patch: Partial<SetupFilter>) => {
    setFilters({ ...filters(), ...patch })
    setFetchFilters({ ...fetchFilters(), ...patch })
    setPage(1)
  }

  const commitSearch = debounce((search: string) => applyNow({ search: search.trim() }), 300)
  const setSearchInput = (value: string) => {
    setSearchInputRaw(value)
    commitSearch(value)
  }
  /** Set the search text without debouncing (e.g. from the URL) */
  const setSearch = (search: string) => {
    commitSearch.clear()
    setSearchInputRaw(search)
    applyNow({ search: search.trim() })
  }
  const setAuthor = (author: string | null) => applyNow({ author })

  const activeFilterCount = () => countActiveFilters(fetchFilters())

  // Keeps the search text and author: those are controlled outside the drawer
  const resetFilters = () => setFilters({ ...defaults, search: filters().search, author: filters().author })
  const areFiltersSet = () => JSON.stringify(filters()) === JSON.stringify(defaults)

  /** Clears the search text and every filter, keeping the sort */
  const clearAll = () => {
    commitSearch.clear()
    setSearchInputRaw('')
    const next = { ...defaults, sort: fetchFilters().sort }
    setFilters(next)
    setFetchFilters(next)
    setPage(1)
  }

  return {
    setDuration,
    fetchFilters,
    runFilters,
    setSymbols,
    areFiltersSet,
    debouncedSetFilters,
    filters,
    page,
    setPatternSpecification,
    resetFilters,
    setAssetType,
    setFilters,
    setSort,
    setPage,
    setPosition,
    setStage,
    setExchanges,
    searchInput,
    setSearchInput,
    setSearch,
    setAuthor,
    applyNow,
    activeFilterCount,
    clearAll,
  }
}
